import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../../configs/api";
import { toast } from "react-toastify";
import TopMoviesSidebar from "../../components/Sidebar/TopMovieSide";
import CommentsSection from "../../components/Comments/CommentsSection";
import TabButton from "../../components/Button/TabButton";
import EpisodeList from "../../components/Episode/Episode";
import CastSection from "../../components/MovieInfor/ActorSection";
import { DetailRecommendationsSection } from "../../components/MovieInfor/RecommendMovie";
import { getProxyUrl } from "../../utils/proxy";
import { Link, Element } from "react-scroll";
import { Helmet } from "react-helmet";

const DetailPhim = () => {
  const [activeTab, setActiveTab] = useState("episodes");
  const { slug } = useParams();
  const [movieData, setMovieData] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [favoriteMovies, setFavoriteMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Lấy title từ localStorage khi component mount
    const savedTitle = localStorage.getItem(`movie-title-${slug}`);
    if (savedTitle) {
      document.title = savedTitle;
    }

    if (!slug) return;

    axios
      .get(`${BASE_URL}/api/phim/${slug}`)
      .then((res) => {
        const phim = res.data;
        setMovieData(phim);

        if (phim.ten_phim) {
          const title = `Xem Phim ${phim.ten_phim}`;
          document.title = title;
          localStorage.setItem(`movie-title-${slug}`, title);
        }
      })
      .catch(() => {
        setMovieData(null);
      });
  }, [slug]);

  useEffect(() => {
    // Nếu đang ở path "/" thì reset title
    if (location.pathname === "/") {
      // Clear localStorage liên quan đến phim
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("movie-title-")) {
          localStorage.removeItem(key);
        }
      });
      document.title = "MePhim - Xem Phim Online Miễn Phí!";
    }
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setIsLoggedIn(true);
      axios
        .get(`${BASE_URL}/api/yeuthich/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setFavoriteMovies(res.data || []);
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            localStorage.removeItem("accessToken");
            setIsLoggedIn(false);
            setFavoriteMovies([]);
          } else {
            setFavoriteMovies([]);
          }
        });
    } else {
      setIsLoggedIn(false);
      setFavoriteMovies([]);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && Array.isArray(favoriteMovies) && slug) {
      const favorited = favoriteMovies.some(
        (movie) => movie.phim && movie.phim.slug === slug
      );
      setIsFavorited(favorited);
    } else {
      setIsFavorited(false);
    }
  }, [favoriteMovies, slug, isLoggedIn]);

  useEffect(() => {
    if (!slug) {
      setMovieData(null);
      setLoading(false);
      return;
    }
    const fetchPhim = async () => {
      setLoading(true);
      setActiveTab("episodes");
      try {
        const res = await axios.get(`${BASE_URL}/api/phim/${slug}/`);
        setMovieData(res.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPhim();
  }, [slug]);

  const handleRemoveMovie = (slug) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    axios
      .post(
        `${BASE_URL}/api/yeuthich/remove-phim/`,
        { phim_slug: slug },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        toast.success("Xóa phim khỏi danh sách yêu thích thành công!");
        setFavoriteMovies(
          favoriteMovies.filter((item) => item.phim.slug !== slug)
        );
      })
      .catch((err) => {
        toast.error("Lỗi xóa phim khỏi danh sách yêu thích");
      });
  };

  const handleAddMovie = (movieSlug) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Vui lòng đăng nhập.");
      return;
    }

    axios
      .post(
        `${BASE_URL}/api/yeuthich/add-phim/`,
        { phim_slug: movieSlug },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        toast.success("Đã thêm vào yêu thích!");
        if (res.data && res.data.phim) {
          setFavoriteMovies((favs) => [...favs, res.data]);
        } else {
          setIsFavorited(true);
        }
      })
      .catch(() => {});
  };

  const handleXemNgay = async () => {
    setLoading(true);
    try {
      let tapPhim = null;
      let ngonNguUsed = "vietsub";

      try {
        const res = await axios.get(
          `${BASE_URL}/api/video-nguon/phim/${slug}/tap/1/vietsub/`
        );
        tapPhim = res.data;
      } catch (error) {
        ngonNguUsed = "thuyetminh";
        const resThuyetMinh = await axios.get(
          `${BASE_URL}/api/video-nguon/phim/${slug}/tap/1/thuyetminh/`
        );
        tapPhim = resThuyetMinh.data;
      }

      if (!tapPhim || !tapPhim.link_video || !tapPhim.so_tap) {
        toast.info("Chưa có tập phim nào được cập nhật!");
        return;
      }

      navigate(
        `/xem-phim/${slug}/tap/${tapPhim.so_tap}?id=${tapPhim.id}&ngonngu=${ngonNguUsed}`
      );
    } catch (error) {
      toast.error("Đã xảy ra lỗi khi tải tập phim.");
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    if (!movieData) return null;
    switch (activeTab) {
      case "episodes":
        return (
          <EpisodeList tenPhim={movieData.ten_phim} slug={movieData.slug} />
        );
      case "actors":
        return (
          <CastSection
            tenPhim={movieData.ten_phim}
            tenKhac={movieData.ten_khac}
          />
        );

      case "suggestions":
        return <DetailRecommendationsSection slug={movieData.slug} />;
      default:
        return null;
    }
  };
  const fullUrl = window.location.href;

  const handleShare = () => {
    const appId = "677996038313263";
    const shareUrl = `https://www.facebook.com/dialog/send?app_id=${appId}&link=${encodeURIComponent(
      fullUrl
    )}&redirect_uri=${encodeURIComponent(fullUrl)}`;
    window.open(shareUrl, "messenger-share-dialog", "width=800,height=600");
  };

  if (error || !movieData) {
    return;
  }

  return (
    <>
      <Helmet>
        <title>MePhim | Xem Phim {movieData.ten_phim}</title>
        <meta property="og:title" content={`Xem Phim ${movieData.ten_phim}`} />
        <meta
          property="og:description"
          content={movieData.mo_ta || "Xem phim online miễn phí"}
        />
        <meta property="og:image" content={movieData.hinh_anh} />
        <meta property="og:url" content={`https://mephim.com/phim/${slug}`} />
        <meta property="og:type" content="video.movie" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="px-4 xl:container xl:mx-auto text-gray-300 min-h-screen">
        {movieData.banner_url && (
          <div
            className="relative w-full h-[30vh] sm:h-[35vh] md:h-[40vh] lg:h-[65vh] bg-cover bg-center"
            style={{
              backgroundImage: `url(${movieData.banner_url})`,
              backgroundPosition: "center",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 5%, rgba(0,0,0,0))",
              WebkitMaskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 5%, rgba(0,0,0,0))",
              maskSize: "100% 100%",
              maskRepeat: "no-repeat",
            }}
            initial={{ opacity: 0, filter: "blur(20px)", scale: 1.02 }}
            animate={{ opacity: 1, filter: "blur(8px)", scale: 1 }}
            exit={{ opacity: 0, filter: "blur(20px)", scale: 1.02 }}
            transition={{
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1],
            }}
            aria-hidden="true"
          >
            <div
              className="hidden md:block absolute inset-0 z-10 pointer-events-none"
              style={{
                backgroundImage: `radial-gradient(#000000 1px, transparent 1px)`,
                backgroundSize: "3px 3px",
                opacity: 0.3,
              }}
            ></div>
          </div>
        )}

        <div className="xl:container xl:mx-auto p-2 pb-16 sm:px-6">
          <div
            className={`relative flex flex-col md:flex-row gap-x-10 lg:gap-10 xl:gap-12 z-20 ${
              movieData.banner_url
                ? "-mt-28 sm:-mt-36 md:-mt-44 lg:-mt-56"
                : "pt-8"
            }`}
          >
            <div className="w-full md:w-[30%] lg:w-[28%] xl:w-[25%] flex-shrink-0">
              <div className="flex flex-col items-center md:items-start">
                <div className="relative w-[180px] h-[270px] lg:w-[190px] mb-4 shadow-xl rounded-lg overflow-hidden">
                  <img
                    src={movieData.poster_url || getProxyUrl(movieData.poster)}
                    alt={`Poster phim ${movieData.ten_phim}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                <h1 className="text-xl lg:text-2xl font-bold text-white mb-1 leading-tight font-seogoe text-center sm:text-left">
                  {movieData.ten_phim}
                </h1>
                {movieData.ten_khac && (
                  <p className="text-sm text-white/60 mb-3 text-center sm:text-left">
                    {movieData.ten_khac}
                  </p>
                )}

                <div className="hidden sm:flex flex-col items-center md:items-start space-y-3 md:space-y-4 text-white w-full mt-2 justify-center">
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-2.5 gap-y-2.5 text-sm text-gray-300">
                    {movieData.ngon_ngu && (
                      <span
                        className="flex items-center px-2 py-0.5 rounded text-xs font-bold text-white text-center"
                        style={{
                          background:
                            "linear-gradient(114.88deg, #f04e23 0%, #c13200 97%)",
                        }}
                      >
                        {movieData.ngon_ngu}
                      </span>
                    )}

                    {movieData.thoi_luong && (
                      <span className="border border-gray-600 px-2 py-0.5 rounded text-xs font-medium">
                        {movieData.thoi_luong}
                      </span>
                    )}
                    {movieData.nam_phat_hanh && (
                      <span className="border border-gray-600 px-2 py-0.5 rounded text-xs font-medium">
                        {movieData.nam_phat_hanh}
                      </span>
                    )}

                    {!movieData.do_tuoi && (
                      <span className="border border-gray-600 px-2 py-0.5 rounded text-xs font-medium">
                        13+
                      </span>
                    )}
                  </div>
                  {/* Tags Thể loại */}
                  <div className="flex flex-wrap gap-2 mb-0 sm:mb-4 md:mb-4">
                    {movieData.the_loai && movieData.the_loai.length > 0 ? (
                      movieData.the_loai.slice(0, 4).map((genre) => (
                        <Link
                          key={genre.slug}
                          className="hidden sm:block px-2 py-1 bg-orange-200/40 hover:bg-orange-300/60  text-white text-xs font-medium rounded-lg backdrop-blur-sm border border-white/20 transition-all duration-500 hover:scale-105 cursor-pointer"
                        >
                          {genre.ten}
                        </Link>
                      ))
                    ) : (
                      <span className="hidden sm:block px-2 py-1 bg-orange-200/40 hover:bg-orange-300/60  text-white text-xs font-medium rounded-lg backdrop-blur-sm border border-white/20 transition-all duration-500 hover:scale-105">
                        Đang cập nhật
                      </span>
                    )}
                  </div>
                </div>
                {movieData.tinh_trang && (
                  <div className="hidden sm:block my-3 text-center">
                    {(() => {
                      const tinhTrang = movieData.tinh_trang
                        .toLowerCase()
                        .trim();
                      const isCompleted =
                        tinhTrang.includes("hoàn tất") ||
                        tinhTrang.includes("full");
                      return (
                        <span
                          className={`inline-flex items-center gap-2 ${
                            isCompleted
                              ? "bg-[#22cb4c1a] text-[#22cb4c]"
                              : "bg-[#ff83001a] text-[#ff8300]"
                          } border-green-700 text-xs font-medium px-6 py-2.5 rounded-full`}
                        >
                          {isCompleted ? (
                            <svg
                              className="w-4 h-4 text-[#22cb4c]"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3}
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          ) : (
                            <div className="w-3 h-3 border-2 border-[#ff8300] border-t-transparent rounded-full animate-spin [animation-duration:2  s]"></div>
                          )}
                          Tình trạng: {movieData.tinh_trang}
                        </span>
                      );
                    })()}
                  </div>
                )}
                <div className="hidden md:block space-y-2.5 text-sm mt-4 lg:mt-2 w-full text-gray-400">
                  {movieData.mo_ta && (
                    <div>
                      <h4 className="font-semibold text-base text-white mb-1.5">
                        Nội dung phim:
                      </h4>
                      <p className="leading-relaxed line-clamp-6">
                        {movieData.mo_ta}
                      </p>
                    </div>
                  )}
                  {movieData.thoi_luong && (
                    <p>
                      <strong className=" text-gray-200 font-medium w-[6rem] inline-block">
                        Thời lượng:
                      </strong>
                      {movieData.thoi_luong}
                    </p>
                  )}
                  {movieData.quoc_gia && (
                    <p>
                      <strong className="text-gray-200 font-medium w-20 inline-block">
                        Quốc gia:
                      </strong>
                      {movieData.quoc_gia}
                    </p>
                  )}
                  {movieData.dao_dien && (
                    <p>
                      <strong className="text-gray-200 font-medium w-20 inline-block">
                        Đạo diễn:
                      </strong>
                      {movieData.dao_dien}
                    </p>
                  )}
                  {movieData.dien_vien && (
                    <p>
                      <strong className="text-gray-200 font-medium w-20 inline-block">
                        Đạo diễn:
                      </strong>
                      {movieData.dien_vien}
                    </p>
                  )}
                </div>
                <TopMoviesSidebar />
              </div>
            </div>
            <div className="w-full md:w-[70%] lg:w-[75%] min-w-0">
              <div className="w-full md:w-[70%] lg:w-[100%] min-w-0">
                <div className="flex flex-wrap items-center justify-center md:justify-start mb-6 gap-y-4 gap-x-6 sm:gap-x-8 lg:gap-x-10">
                  <button
                    onClick={handleXemNgay}
                    className="flex items-center justify-center px-6 py-3 sm:px-6 sm:py-4 space-x-2 rounded-full text-white font-semibold text-sm sm:text-base transition duration-200 shadow-md hover:brightness-105"
                    style={{
                      background:
                        "linear-gradient(114.88deg, #f04e23 0%, #c13200 97%)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      className="mr-2"
                    >
                      <path
                        fill="currentColor"
                        d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.737 4 21.277 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648z"
                      />
                    </svg>
                    <span>Xem Ngay</span>
                  </button>

                  <div className="flex items-center gap-8">
                    {/* Yêu thích */}
                    <div className="relative group">
                      <button
                        className="flex flex-col items-center justify-center p-2 gap-1 rounded-lg group cursor-pointer"
                        onClick={() => {
                          if (!isLoggedIn) {
                            toast.error("Bạn chưa đăng nhập!");
                            setIsFavorited(false);
                            return;
                          }
                          if (isFavorited) {
                            handleRemoveMovie(slug);
                            setIsFavorited(false);
                          } else {
                            handleAddMovie(slug);
                            setIsFavorited(true);
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          className={`mb-1 group-hover:text-orange-500 ${
                            isFavorited ? "text-orange-500" : "text-white"
                          }`}
                        >
                          <path
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12 3.942a6.35 6.35 0 0 1 4.305-1.692c1.726 0 3.374.71 4.58 1.96a6.7 6.7 0 0 1 1.865 4.652a6.7 6.7 0 0 1-1.865 4.652c-.796.825-1.591 1.67-2.39 2.518c-1.624 1.724-3.265 3.467-4.97 5.108l-.003.004a2.213 2.213 0 0 1-3.113-.069l-7.295-7.561c-2.485-2.577-2.485-6.727 0-9.303A6.33 6.33 0 0 1 12 3.942"
                          />
                        </svg>
                        <span className="text-[12px] sm:text-[14px] text-white font-medium text-center">
                          Yêu thích
                        </span>
                      </button>
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-white text-black text-xs px-3 py-1 rounded shadow-md opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-10 whitespace-nowrap">
                        {isFavorited
                          ? "Xóa khỏi danh sách yêu thích"
                          : "Thêm vào danh sách yêu thích"}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                      </div>
                    </div>

                    {/* Chia sẻ */}
                    <button
                      className="flex flex-col items-center justify-center p-2 gap-1 rounded-lg group cursor-pointer"
                      onClick={handleShare}
                      title="Chia sẻ qua facebook"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        className="mb-1 group-hover:text-orange-500 text-white"
                      >
                        <path
                          fill="currentColor"
                          d="m10.114 4.491l-.203 3.144l-.02.417l-.09.01C5.363 8.582 2 12.366 2 17q0 .686.102 1.357c.279 1.845.489 2.024 1.772.498a9 9 0 0 1 1.04-1.03a7.96 7.96 0 0 1 4.754-1.818l.226-.005l.061 1.229l.166 2.345c.08.804.926 1.353 1.704.914c.352-.198.695-.41 1.04-.62c1.787-1.118 3.46-2.403 5.09-3.738c.96-.8 1.8-1.558 2.516-2.248c.33-.323.66-.646.979-.98c.462-.484.508-1.285.024-1.792c-1.114-1.165-2.688-2.624-4.647-4.172c-1.588-1.242-3.23-2.402-4.97-3.421c-.837-.477-1.667.177-1.743.972"
                        />
                      </svg>
                      <span className="text-[12px] sm:text-[14px] font-medium text-white text-center">
                        Chia sẻ
                      </span>
                    </button>

                    {/* Bình luận */}
                    <Link
                      to="comments-section"
                      smooth={true}
                      duration={800}
                      className="flex flex-col items-center justify-center p-2 gap-1 rounded-lg group cursor-pointer"
                      style={{ cursor: "pointer" }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        className="mb-1 group-hover:text-orange-500 text-white"
                      >
                        <path
                          fill="currentColor"
                          fillRule="evenodd"
                          d="M2 6a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-4.172a1 1 0 0 0-.707.293l-1.867 1.867C11.054 22.361 9 21.51 9 19.812A.81.81 0 0 0 8.188 19H5a3 3 0 0 1-3-3zm5 0a1 1 0 0 0 0 2h10a1 1 0 1 0 0-2zm0 4a1 1 0 1 0 0 2h10a1 1 0 1 0 0-2zm0 4a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-[12px] sm:text-[14px] font-medium text-white text-center">
                        Bình luận
                      </span>
                    </Link>

                    {/* Đánh giá */}
                    {/* <div className="flex flex-col items-center justify-center p-2 gap-1.5 rounded-lg group cursor-default">
                    <VoteRating
                      tenKiem={movieData.ten_phim || movieData.ten_khac}
                      onVoteLoaded={handleVoteLoaded(movieData.id)}
                    />
                    {ratings[movieData.id] != null ? (
                      <>
                        <FcRating size={18} />
                        <p className="text-[16px] leading-none text-white font-bold">
                          {ratings[movieData.id].toFixed(1)}
                        </p>
                      </>
                    ) : (
                      <>
                        <FcRating size={18} />
                        <p className="text-[16px] leading-none text-white font-bold">
                          N/a
                        </p>
                      </>
                    )}
                  </div> */}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-700 mb-5">
                <nav className="-mb-px flex overflow-x-auto scrollbar-hide">
                  <TabButton
                    text="Tập phim"
                    active={activeTab === "episodes"}
                    onClick={() => setActiveTab("episodes")}
                  />
                  <TabButton
                    text="Diễn viên"
                    active={activeTab === "actors"}
                    onClick={() => setActiveTab("actors")}
                  />
                  <TabButton
                    text="Đề xuất"
                    active={activeTab === "suggestions"}
                    onClick={() => setActiveTab("suggestions")}
                  />
                </nav>
              </div>
              <div className="tab-content mb-8">{renderTabContent()}</div>
              <CommentsSection />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailPhim;
